import { ReactElement, ReactNode } from 'react';

import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { capitalise, formatDateTo, isDarkMode } from '@localstack/services';
import { SeatHolderActivity, SortDirection, UsageInsightSortField } from '@localstack/types';
import { GridComponent, UserAvatar, UserIdentity, VirtualizedGridList } from '@localstack/ui';

import { hasActiveDate } from './utils';
import { HeaderCell } from './UsageColumnCell';

const HORIZONTAL_PADDING = 2;

const HeaderRow = styled(Box)(({ theme }) => ({
  backgroundColor: isDarkMode(theme) ? theme.palette.background.paper : theme.palette.grey[100],
  borderRadius: theme.spacing(10),
}));

type UsageTableColumn = {
  key: string;
  header: ReactNode;
  width: string;
  sortField?: UsageInsightSortField;
  tooltip?: string;
  render: (row: SeatHolderActivity) => ReactNode;
};

const getUsageColumns = (totalRows: number): UsageTableColumn[] => [
  {
    key: 'name',
    header: `Name ${totalRows}`,
    width: 'minmax(220px, 320px)',
    render: (row) => (
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
        <UserAvatar email={row.email} />
        <UserIdentity fullName={row.full_name} email={row.email} />
      </Stack>
    ),
  },
  { key: 'spacer', header: '', width: '1fr', render: () => null },
  {
    key: 'role',
    header: 'Role',
    width: 'minmax(100px, max-content)',
    render: (row) => (
      <Typography variant="body2" color={row.role ? undefined : 'text.secondary'}>
        {capitalise(row.role ?? 'Removed')}
      </Typography>
    ),
  },
  {
    key: 'active',
    header: 'Last Active',
    width: 'minmax(120px, max-content)',
    sortField: UsageInsightSortField.LAST_ACTIVE_DATE,
    tooltip: 'The last emulator activation registered for this user.',
    render: (row) => (
      <Typography variant="body2">
        {hasActiveDate(row.last_active_date) ? formatDateTo(row.last_active_date, 'MMM D, YYYY') : '—'}
      </Typography>
    ),
  },
];

type UsageTableProps = {
  data: SeatHolderActivity[];
  totalRows: number;
  showHeader: boolean;
  isLoading: boolean;
  hasMore: boolean;
  isLoadingMore?: boolean;
  onLoadMore: () => void;
  sortBy: UsageInsightSortField;
  sortDirection: SortDirection;
  onSortChange: (field: UsageInsightSortField, direction: SortDirection) => void;
  resetScrollKey?: unknown;
};

export const UsageTable = ({
  data,
  totalRows,
  showHeader,
  isLoading,
  hasMore,
  isLoadingMore,
  onLoadMore,
  sortBy,
  sortDirection,
  onSortChange,
  resetScrollKey,
}: UsageTableProps): ReactElement => {
  const theme = useTheme();
  const columns = getUsageColumns(totalRows);
  const gridColumns = columns.map((column) => column.width).join(' ');

  return (
    <Box>
      {showHeader && (
        <HeaderRow>
          <GridComponent columns={gridColumns} py={1} px={2}>
            {columns.map((column) => (
              <Stack key={column.key} direction="row" alignItems="center" spacing={0.5}>
                <HeaderCell column={column} sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
              </Stack>
            ))}
          </GridComponent>
        </HeaderRow>
      )}

      {isLoading && (
        <Stack alignItems="center" py={4}>
          <CircularProgress size={24} />
        </Stack>
      )}

      {!isLoading && data.length === 0 && (
        <Stack alignItems="center" py={4}>
          <Typography variant="body2" color="text.secondary">
            No members found
          </Typography>
        </Stack>
      )}

      {!isLoading && data.length > 0 && (
        <VirtualizedGridList
          items={data}
          columns={gridColumns}
          visibleRowCount={Infinity}
          resetScrollKey={resetScrollKey}
          getRowKey={(row) => row.user_id}
          gridProps={{ px: HORIZONTAL_PADDING, rowGap: theme.spacing(2) }}
          footer={
            hasMore && (
              <Box display="flex" justifyContent="center" py={2}>
                <Button variant="outlined" size="small" onClick={onLoadMore} disabled={isLoadingMore}>
                  {isLoadingMore ? 'Loading…' : 'Load more'}
                </Button>
              </Box>
            )
          }
          renderRow={(row) => (
            <>
              {columns.map((column) => (
                <Box key={column.key}>{column.render(row)}</Box>
              ))}
            </>
          )}
        />
      )}
    </Box>
  );
};
